// DebugViewer.tsx
import React from "react";
import {
  Box,
  Flex,
  Group,
  Button,
  Title,
  Text,
  SegmentedControl,
  Paper,
  Code,
  Stack,
  Divider,
  TextInput,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectDebugActiveBuildingStoryName,
  setDebugMcBimItemsInRedux,
  setActiveBuildingStoryNameInRedux,
  // NEW: use filter + toggle
  selectDebugItemsToShow,
  setItemsToShowInRedux,
  type ItemsToShow,
  setDebugFreeTextSearchInRedux,
  // NEW: bounds selector
} from "../DebugSlice";
import type { IMcBimItem } from "@backend/src/shared/schemas/schemas";
import { DeckBimViewer } from "@multiconsult-group/multiplacer-ui";
import TypeClassHider from "./TypeClassHider";
import "../../index.css";
import {
  selectDebugPixPolygonsFiltered,
  selectViewerPixBounds,
  selectDebugUniqueBuildingStoryNames,
  selectDebugMcItemsWithGeomOnActiveBuildingStory,
  selectDebugCeilingGridPixPathsFromRawLines,
} from "./twoDFloorplanSelectors";
import { MissingPolygonsWarning } from "./MissingPolygonWarning";

type Props = { onLoad?: (json: unknown) => void };

export const Debug2dFloorplan: React.FC<Props> = ({ onLoad }) => {
  const dispatch = useAppDispatch();

  // Filtered polygons for rendering
  const pixPolygons = useAppSelector(selectDebugPixPolygonsFiltered);
  // NEW: bounds for whatâ€™s currently rendered
  const bounds = useAppSelector(selectViewerPixBounds);
  const ceilingLines = useAppSelector(selectDebugCeilingGridPixPathsFromRawLines);
  const zoomExtentKey = React.useMemo(() => {
    if (!bounds) return "debug-2d-floorplan:no-extent";

    const polygonIds = pixPolygons.map((polygon) => polygon.id).join("|");
    const pathIds = ceilingLines.map((path) => path.id).join("|");

    return [
      "debug-2d-floorplan",
      pixPolygons.length,
      ceilingLines.length,
      Math.round(bounds.minX),
      Math.round(bounds.minY),
      Math.round(bounds.maxX),
      Math.round(bounds.maxY),
      polygonIds,
      pathIds,
    ].join(":");
  }, [bounds, ceilingLines, pixPolygons]);

  async function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const text = await file.text();
    const json = JSON.parse(text.replace(/^\uFEFF/, ""));
    onLoad?.(json);

    if (Array.isArray(json)) {
      dispatch(setDebugMcBimItemsInRedux(json as IMcBimItem[]));
    }
  }

  const freeTextSearch = useAppSelector((s) => s.debug.freeTextSearch);

  return (
    <Box h="100%">
      <Flex direction="column" gap="sm" h="100%">
        <Group justify="space-between">
          <Title order={3}>Debug Viewer</Title>
          <Group gap="md">
            <ItemsToShowToggle />
          </Group>
        </Group>
        <TextInput
          value={freeTextSearch}
          onChange={(e) => dispatch(setDebugFreeTextSearchInRedux(e.currentTarget.value))}
        />

        {/* Dropzone */}
        <Box
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          p="md"
          style={{
            border: "2px dashed var(--mantine-color-gray-4)",
            borderRadius: 8,
            textAlign: "center",
            cursor: "grab",
            userSelect: "none",
          }}
        >
          <Text>
            Slipp en <code>.json</code>-fil her
          </Text>
        </Box>

        <MissingPolygonsWarning />

        {/* Etasjevelger + TypeClassHider */}
        <Stack gap="xs">
          <FloorSelector />
          <TypeClassHider />
        </Stack>

        <Divider my={4} />

        {/* Viewer-omrÃ¥de fyller resten av hÃ¸yden */}
        <Box h="100%" w="100%" style={{ flex: 1, minHeight: 0, position: "relative" }}>
          <DeckBimViewer
            flip2dCoordinate={true}
            angle={0}
            polygons={pixPolygons}
            paths={ceilingLines}
            zoomExtentKey={zoomExtentKey}
          />

          {/* --- Bounds overlay (vises kun hvis bounds finnes) --- */}
          {bounds && (
            <Paper
              shadow="sm"
              p="xs"
              radius="sm"
              withBorder
              style={{
                position: "absolute",
                right: 8,
                bottom: 8,
                pointerEvents: "none",
                background: "rgba(255,255,255,0.9)",
              }}
            >
              <Stack gap={2} style={{ fontFamily: "var(--mantine-font-family-monospace)" }}>
                <Code>
                  minX: {Math.round(bounds.minX)} • minY: {Math.round(bounds.minY)}
                </Code>
                <Code>
                  maxX: {Math.round(bounds.maxX)} • maxY: {Math.round(bounds.maxY)}
                </Code>
              </Stack>
            </Paper>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

/** Toggle for "all" | "spaces" | "non-spaces" */
const ItemsToShowToggle: React.FC = () => {
  const dispatch = useAppDispatch();
  const value = useAppSelector(selectDebugItemsToShow);

  return (
    <SegmentedControl
      size="sm"
      value={value}
      onChange={(v) => dispatch(setItemsToShowInRedux(v as ItemsToShow))}
      data={[
        { label: "Alle", value: "all" },
        { label: "Rom", value: "spaces" },
        { label: "Ikke-rom", value: "non-spaces" },
      ]}
    />
  );
};

/** Prop-løs etasjevelger som bruker selectors direkte */
const FloorSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const floors = useAppSelector(selectDebugUniqueBuildingStoryNames);
  const active = useAppSelector(selectDebugActiveBuildingStoryName);
  const countOnActive = useAppSelector(selectDebugMcItemsWithGeomOnActiveBuildingStory).length;

  if (floors.length === 0) return null;

  return (
    <Flex direction="column" gap={4}>
      <Flex align="center" gap="xs" wrap="wrap">
        <Text size="sm" c="dimmed">
          Etasje:
        </Text>

        <Button
          size="xs"
          variant={active == null ? "filled" : "default"}
          color="dark"
          onClick={() => dispatch(setActiveBuildingStoryNameInRedux(null))}
          title="Vis alle"
        >
          Alle
        </Button>

        {floors.map((name) => (
          <Button
            key={name}
            size="xs"
            variant={active === name ? "filled" : "default"}
            color="dark"
            onClick={() => dispatch(setActiveBuildingStoryNameInRedux(name))}
            title={`Filtrér på ${name}`}
          >
            {name}
          </Button>
        ))}
      </Flex>

      <Text size="sm" c="dimmed">
        {active ? `Aktiv etasje: ${active}` : "Ingen etasje valgt (viser alle)."} • Antall:{" "}
        {countOnActive}
      </Text>
    </Flex>
  );
};
